import { cn } from '@recipes/ui';
import { Sparkles } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** After this long the indicator says that waiting is normal, then that it takes unusually long. */
const PATIENCE_AFTER_MS = 6_000;
const SLOW_AFTER_MS = 30_000;

/**
 * Shown while something is being worked on that takes seconds rather than milliseconds – an AI
 * request above all. A spinning button alone does not tell the user whether anything is
 * happening: this names the work, moves, and after a few seconds says that the wait is expected.
 * With `progress` the bar is determinate, without it the bar travels.
 */
export function ProcessingIndicator({
  label,
  progress,
  detail,
  compact = false,
  className,
  children,
}: {
  label: string;
  /** 0–100 when the work reports progress. */
  progress?: number | null;
  /** Replaces the patience hint, e.g. "42 % · checking for updates". */
  detail?: ReactNode;
  /** One line without the bar – for buttons rows, dialogs and chat bubbles. */
  compact?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - started), 1000);
    return () => clearInterval(timer);
  }, []);
  const hint =
    elapsed >= SLOW_AFTER_MS
      ? t('processing.slow')
      : elapsed >= PATIENCE_AFTER_MS
        ? t('processing.patience')
        : null;
  const determinate = typeof progress === 'number';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="processing-indicator"
      className={cn(compact ? 'text-sm' : 'rounded-lg border bg-card p-4', className)}
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <Sparkles
          className="size-4 shrink-0 animate-pulse text-primary motion-reduce:animate-none"
          aria-hidden
        />
        {/* The animated dots replace a trailing ellipsis of the text. */}
        <span>{label.replace(/[.…\s]+$/u, '')}</span>
        <span className="inline-flex gap-0.5" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="size-1 animate-bounce rounded-full bg-current motion-reduce:animate-none"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </span>
      </p>
      {!compact && (
        <div
          className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
          role={determinate ? 'progressbar' : undefined}
          aria-valuemin={determinate ? 0 : undefined}
          aria-valuemax={determinate ? 100 : undefined}
          aria-valuenow={determinate ? Math.round(progress) : undefined}
          aria-hidden={determinate ? undefined : true}
        >
          {determinate ? (
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${Math.min(100, Math.max(5, progress))}%` }}
            />
          ) : (
            <div className="h-full w-2/5 animate-indeterminate rounded-full bg-primary motion-reduce:w-full motion-reduce:animate-pulse" />
          )}
        </div>
      )}
      {(detail || hint) && (
        <p className={cn('text-xs text-muted-foreground', compact ? 'mt-1' : 'mt-2')}>
          {detail}
          {detail && hint ? ' · ' : null}
          {hint}
        </p>
      )}
      {children}
    </div>
  );
}
